import { ApiError } from "../../services/api.js";
import Button from "./Button.jsx";

/** Inline, non-blocking error banner. Keeps the page alive so data stays visible. */
export function Alert({ error, onRetry, className = "" }) {
  if (!error) return null;

  const message =
    error instanceof ApiError
      ? [error.message, ...error.fieldErrors].filter(Boolean)[0]
      : typeof error === "string"
        ? error
        : error?.message || "Something went wrong.";

  const details =
    error instanceof ApiError && error.fieldErrors.length > 1
      ? error.fieldErrors.slice(1)
      : [];

  return (
    <div
      role="alert"
      className={`flex items-start gap-4 rounded-xl bg-error-container px-5 py-4 text-on-error-container ${className}`}
    >
      <span className="material-symbols-outlined text-xl" aria-hidden="true">
        error
      </span>
      <div className="flex-1">
        <p className="text-sm font-semibold">{message}</p>
        {details.length > 0 && (
          <ul className="mt-1 list-inside list-disc text-xs opacity-80">
            {details.map((detail) => (
              <li key={detail}>{detail}</li>
            ))}
          </ul>
        )}
      </div>
      {onRetry && (
        <Button variant="ghost" size="sm" onClick={onRetry}>
          Retry
        </Button>
      )}
    </div>
  );
}

/**
 * Success / info / warning / error banner.
 *
 * `error` is the one an optimistic update falls back to: the UI already rolled
 * itself back, so this is only here to explain *why* nothing changed.
 */
export function Notice({ message, tone = "success", onDismiss, className = "" }) {
  if (!message) return null;
  const tones = {
    success: "bg-tertiary-fixed text-on-tertiary-fixed-variant",
    info: "bg-secondary-container text-on-secondary-container",
    warning: "bg-primary-fixed text-on-primary-fixed-variant",
    error: "bg-error-container text-on-error-container",
  };
  const icons = {
    success: "check_circle",
    info: "info",
    warning: "warning",
    error: "error",
  };
  return (
    <div
      role={tone === "error" ? "alert" : "status"}
      className={`flex items-center gap-3 rounded-xl px-5 py-3 text-sm font-medium animate-slide-down ${
        tones[tone] ?? tones.success
      } ${className}`}
    >
      <span className="material-symbols-outlined text-lg" aria-hidden="true">
        {icons[tone] ?? icons.success}
      </span>
      <span className="flex-1">{message}</span>
      {onDismiss && (
        <button type="button" onClick={onDismiss} aria-label="Dismiss">
          <span className="material-symbols-outlined text-base">close</span>
        </button>
      )}
    </div>
  );
}

export function Spinner({ className = "" }) {
  return (
    <div className={`flex items-center justify-center py-16 ${className}`}>
      <span
        className="material-symbols-outlined animate-spin text-4xl text-primary"
        aria-hidden="true"
      >
        progress_activity
      </span>
      <span className="sr-only">Loading</span>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Skeletons — theme-matched placeholders that mimic the shape of real content */
/* so the page barely moves when data lands.                                   */
/* -------------------------------------------------------------------------- */

/** Base pulse block. Everything below is composed from this. */
export function Skeleton({ className = "" }) {
  return (
    <div
      className={`animate-pulse rounded-2xl bg-surface-container ${className}`}
      aria-hidden="true"
    />
  );
}

/** A paragraph placeholder: n lines, last one short so it reads as text. */
export function SkeletonText({ lines = 3, className = "" }) {
  return (
    <div className={`space-y-2 ${className}`} aria-hidden="true">
      {Array.from({ length: lines }).map((_, index) => (
        <div
          key={index}
          className={`h-3.5 animate-pulse rounded-full bg-surface-container ${
            index === lines - 1 && lines > 1 ? "w-2/3" : "w-full"
          }`}
        />
      ))}
    </div>
  );
}

/** Avatar disc — matches the `h-9/h-12 w-9/h-12 rounded-full` avatars. */
export function SkeletonAvatar({ className = "h-12 w-12" }) {
  return (
    <div
      className={`flex-shrink-0 animate-pulse rounded-full bg-surface-container ${className}`}
      aria-hidden="true"
    />
  );
}

/** Stat tile placeholder — mirrors StatCard's icon block + label/value/hint. */
export function SkeletonStatCard({ className = "" }) {
  return (
    <div
      className={`card-surface flex items-center gap-5 p-6 ${className}`}
      aria-hidden="true"
    >
      <div className="h-14 w-14 flex-shrink-0 animate-pulse rounded-xl bg-surface-container" />
      <div className="flex-1 space-y-2.5">
        <div className="h-3 w-24 animate-pulse rounded-full bg-surface-container" />
        <div className="h-7 w-20 animate-pulse rounded-full bg-surface-container" />
        <div className="h-3 w-32 animate-pulse rounded-full bg-surface-container" />
      </div>
    </div>
  );
}

/** Directory row — avatar + two lines + optional trailing action pills. */
export function SkeletonListItem({ actions = true, className = "" }) {
  return (
    <li
      className={`flex flex-wrap items-center gap-5 px-8 py-5 ${className}`}
      aria-hidden="true"
    >
      <div className="h-12 w-12 flex-shrink-0 animate-pulse rounded-xl bg-surface-container" />
      <div className="min-w-0 flex-1 space-y-2">
        <div className="h-4 w-40 max-w-[60%] animate-pulse rounded-full bg-surface-container" />
        <div className="h-3 w-56 max-w-[80%] animate-pulse rounded-full bg-surface-container" />
      </div>
      {actions && (
        <div className="hidden shrink-0 items-center gap-2 sm:flex">
          <div className="h-9 w-16 animate-pulse rounded-lg bg-surface-container" />
          <div className="h-9 w-9 animate-pulse rounded-lg bg-surface-container" />
          <div className="h-9 w-9 animate-pulse rounded-lg bg-surface-container" />
        </div>
      )}
    </li>
  );
}

/** Full table placeholder. The first column mimics an avatar + name cell. */
export function SkeletonTable({ rows = 6, columns = 6, className = "" }) {
  return (
    <table
      className={`w-full border-collapse text-left ${className}`}
      aria-hidden="true"
    >
      <thead>
        <tr className="bg-surface-container-low/50">
          {Array.from({ length: columns }).map((_, index) => (
            <th key={index} className="px-6 py-4">
              <div className="h-3 w-16 animate-pulse rounded-full bg-surface-container" />
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: rows }).map((_, rowIndex) => (
          <tr key={rowIndex} className="row-stripe">
            {Array.from({ length: columns }).map((_, colIndex) => (
              <td key={colIndex} className="px-6 py-5">
                {colIndex === 0 ? (
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 flex-shrink-0 animate-pulse rounded-full bg-surface-container" />
                    <div className="space-y-2">
                      <div className="h-3.5 w-32 animate-pulse rounded-full bg-surface-container" />
                      <div className="h-3 w-24 animate-pulse rounded-full bg-surface-container" />
                    </div>
                  </div>
                ) : (
                  <div
                    className={`h-4 animate-pulse rounded-full bg-surface-container ${
                      colIndex > 1 ? "w-16" : "w-24"
                    }`}
                  />
                )}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Weekly-chart placeholder: seven pulsing bars on the same baseline. */
export function SkeletonChartBars({ bars = 7, className = "" }) {
  const heights = ["45%", "72%", "38%", "88%", "58%", "96%", "62%"];
  return (
    <div className={`flex h-48 items-end gap-3 ${className}`} aria-hidden="true">
      {Array.from({ length: bars }).map((_, index) => (
        <div key={index} className="flex flex-1 justify-center">
          <div
            className="w-full animate-pulse rounded-t-lg bg-surface-container"
            style={{ height: heights[index % heights.length] }}
          />
        </div>
      ))}
    </div>
  );
}

/** Ranked progress list placeholder (Top Services / Top Employees). */
export function SkeletonProgressList({ rows = 4, className = "" }) {
  return (
    <ul className={`space-y-5 ${className}`} aria-hidden="true">
      {Array.from({ length: rows }).map((_, index) => (
        <li key={index} className="space-y-2">
          <div className="flex items-center justify-between gap-4">
            <div className="h-4 w-32 animate-pulse rounded-full bg-surface-container" />
            <div className="h-4 w-16 animate-pulse rounded-full bg-surface-container" />
          </div>
          <div className="h-2 w-full animate-pulse rounded-full bg-surface-container" />
        </li>
      ))}
    </ul>
  );
}

export function EmptyState({ icon = "search_off", title, message, action }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 px-6 py-20 text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-surface-container text-on-surface-variant">
        <span className="material-symbols-outlined text-3xl" aria-hidden="true">
          {icon}
        </span>
      </div>
      <h3 className="font-headline text-lg font-bold text-on-surface">{title}</h3>
      {message && (
        <p className="max-w-sm text-sm text-on-surface-variant">{message}</p>
      )}
      {action}
    </div>
  );
}

export default Alert;
